import { isAllChildrenSelected, updateAllChildren, updateParentIsAllChildrenSelected } from '@/components/devicecontrol/VelocityConfiguration/Roles/helper';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Spin, Tree, notification } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { EventDataNode } from 'antd/lib/tree';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { findMockedNodes, findNode, getDeviceObjectTypeBy, getExpandedChildrenOfRootNode, handleResponse } from '../../../Helper';
import { graphicApi } from '../../../api';
import { useDraggable } from '../../../hooks/useDraggable';
import { DictionaryCustom, TupleTyped } from '../../../model/CommonModel';
import { CurrentDeviceControlObj, ObjectTypes } from '../../../model/DeviceAdminModel';
import {
	ActionToExecute,
	Coordinate,
	DirectoryTreeOnExpandParams,
	FloatingWindowsVisibility,
	ObjectLibraryFolder,
	ObjectLibraryTreeNode,
} from '../../../model/GraphicModel';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import { setCurrentDevice } from '../../../store/common/actions';
import { selectCurrentDevice } from '../../../store/common/selectors';
import {
	setDropDataNodesAction,
	setErrorMessageAction,
	setLastObjectLibraryCoordinatesAction,
	setLastPlottedDevicesCoordinatesAction,
	switchFloatingWindowVisibilityAction,
} from '../../../store/graphic/actions';
import { dropDataNodesSelector, errorMessagesSelector } from '../../../store/graphic/selectors';
import { ModalConfirmation } from '../../common';
import {
	buildObjectLibraryTree,
	createOptionsObjectLibrary,
	getConfigureDeviceModal,
	isValidToLoadDevices,
	keywordFilter,
	loadDragImages,
	mapDeviceRenderNode,
	resetCheckedObjectLibraryTree,
} from '../helper';
import styles from './objectLibrary.module.scss';

const { DirectoryTree } = Tree;
const { Search } = Input;

type NodeDragEvent<T = HTMLDivElement> = {
	event: React.DragEvent<T>;
	node: EventDataNode<ObjectLibraryTreeNode>;
};

type Props = {
	data: ObjectLibraryTreeNode;
	isObjectDirectory: boolean;
	initialCoordinates: Coordinate;
	parentContainerId: string;
	parentCoordinates: Coordinate;
};

const ObjectLibrary: React.FC<Props> = ({ data, isObjectDirectory, initialCoordinates, parentContainerId, parentCoordinates }) => {
	//#region state management variables
	const dispatch = useStoreDispatch();
	const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
	const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
	const [isLoading, setIsLoading] = useState(true);
	const [searchValue, setSearchValue] = useState<string>('');
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const [checkedDropDataNodes, setCheckedDropDataNodes] = useState<ObjectLibraryTreeNode[]>([]);
	const [currentDeviceForConfiguration, setCurrentDeviceForConfiguration] = useState<JSX.Element>(null);
	const [objectLibrary, setObjectLibrary] = useState<ObjectLibraryTreeNode>(data);
	const [dragImages, setDragImages] = useState<DictionaryCustom<ObjectTypes, HTMLImageElement>[]>();
	const currentDevice: CurrentDeviceControlObj = useStoreSelector<CurrentDeviceControlObj>(selectCurrentDevice);
	const errorMessages: TupleTyped<number, string>[] = useStoreSelector(errorMessagesSelector);
	let dropDataNodes: ObjectLibraryTreeNode[] = undefined;
	if (isObjectDirectory) {
		dropDataNodes = useStoreSelector(dropDataNodesSelector);
	}
	//#endregion

	const controllerControlName = isObjectDirectory ? 'ObjectDirectory' : 'PlottedLibrary';

	useDraggable({
		parentContainerId,
		targetContainerId: `graphic${controllerControlName}`,
		targetDragContainerId: `graphic${controllerControlName}Content`,
		initialCoordinates,
		parentCoordinates,
		onDragEnd: (coordinate: Coordinate) => {
			if (isObjectDirectory) {
				dispatch(setLastObjectLibraryCoordinatesAction(coordinate));
			} else {
				dispatch(setLastPlottedDevicesCoordinatesAction(coordinate));
			}
		},
	});

	useEffect(() => {
		if (isObjectDirectory) {
			setDragImages(loadDragImages());
		}

		if (objectLibrary) {
			buildObjectLibraryTree(objectLibrary);
			batch(() => {
				setExpandedKeys(getExpandedChildrenOfRootNode(objectLibrary));
				setObjectLibrary(objectLibrary);
			});
		}
		setIsLoading(false);
	}, []);

	useEffect(() => {
		if (!dropDataNodes?.length) {
			setCheckedDropDataNodes([]);
		}
	}, [dropDataNodes]);

	const reassignParentNode = (
		childNode: ObjectLibraryTreeNode,
		parentNode: ObjectLibraryTreeNode,
		isFolderChecked: boolean,
		createOptions: boolean = true
	) => {
		const { children } = childNode;

		childNode.parentNode = parentNode;
		if (childNode.folderType === ObjectLibraryFolder.FOLDER && isFolderChecked) {
			for (let i = 0; i < children.length; i++) {
				updateAllChildren(children[i], true);
			}
		}

		for (let i = 0; i < children.length; i++) {
			if (createOptions && !children[i].options) {
				children[i].options = createOptionsObjectLibrary(childNode.children[i]);
			}

			reassignParentNode(children[i], childNode, isFolderChecked);
		}
	};

	const handleOnExpand = (expandedKeys: React.Key[], params: DirectoryTreeOnExpandParams) => {
		const currentNode: ObjectLibraryTreeNode = params.node;

		if (currentNode.folderType === ObjectLibraryFolder.FOLDER) {
			const hasMockedChildren = currentNode.children.findIndex(child => child.extendedData.hasMockedData) !== -1;
			if (hasMockedChildren && isValidToLoadDevices(currentNode.extendedData.objectType)) {
				setIsLoading(true);
				graphicApi
					.getObjectsLibraryExpanded(
						currentNode.parentNode.extendedData.objectId,
						currentNode.extendedData.objectType,
						currentNode.extendedData.portStyle
					)
					.then(response => {
						if (handleResponse(response)) {
							dispatch(setErrorMessageAction(response.ErrorMessage));
							return;
						}
						const rootNode = { ...objectLibrary };
						const newNode = findNode(rootNode, {
							ObjectType: currentNode.extendedData.objectType,
							ParentId: currentNode.parentNode.extendedData.objectId,
							ObjectId: currentNode.extendedData.objectId,
							PortStyle: currentNode.extendedData.portStyle,
						});
						newNode.extendedData.hasMockedData = false;
						newNode.children = [...response.Entity];
						reassignParentNode(newNode, newNode.parentNode, newNode.isChecked);
						isAllChildrenSelected(newNode);

						setObjectLibrary(rootNode);
					})
					.finally(() => {
						batch(() => {
							setExpandedKeys(expandedKeys);
							setAutoExpandParent(false);
							setIsLoading(false);
						});
					});
			} else {
				setExpandedKeys(expandedKeys);
			}
		} else {
			setExpandedKeys(expandedKeys);
		}
	};

	const handleOnClickContextMenu = (sender: ObjectLibraryTreeNode) => {
		const {
			extendedData: { actionToExecute },
		} = sender;
		switch (actionToExecute) {
			case ActionToExecute.Delete:
				confirmObjectDeletion(sender);
				break;
			case ActionToExecute.ConfigureDevice:
				openDeviceToConfigure(sender);
				break;
			case ActionToExecute.Refresh:
				performRefreshTopology();
				break;
		}
	};

	const performRefreshTopology = async () => {
		setIsLoading(true);
	};

	const handleOnCloseOpenDevice = () => {
		setCurrentDeviceForConfiguration(null);
		const newDevice = { Id: 0, DeviceObjectType: currentDevice.DeviceObjectType, IsModalOpen: false };
		dispatch(setCurrentDevice(newDevice));
	};

	const openDeviceToConfigure = (objectSelected: ObjectLibraryTreeNode) => {
		const { objectId, objectType, address } = objectSelected.extendedData;

		const newDevice: CurrentDeviceControlObj = {
			...currentDevice,
			Id: objectId,
			Address: address,
			IsModalOpen: !currentDevice.IsModalOpen,
			IsNew: false,
			DeviceObjectType: getDeviceObjectTypeBy(objectType),
		};
		dispatch(setCurrentDevice(newDevice));

		const device = getConfigureDeviceModal({
			deviceInfo: newDevice,
			setRedirectPage: () => {},
			setShouldResetSearchColumn: () => {},
			handleCancel: handleOnCloseOpenDevice,
			handleSave: handleOnCloseOpenDevice,
		});

		setCurrentDeviceForConfiguration(device);
	};

	const confirmObjectDeletion = async (objectSelected: ObjectLibraryTreeNode) => {
		const { folderType } = objectSelected;
		const hasChildren = objectLibrary.children !== undefined && objectLibrary.children.length;

		if (folderType === ObjectLibraryFolder.FOLDER) {
			if (hasChildren) {
				const errorMessage = errorMessages.find(msg => msg.Item1 === 2516);
				notification['error']({
					message: errorMessage?.Item2,
				});
			}
		} else {
			const errorMessage = errorMessages.find(msg => msg.Item1 === 2136);
			ModalConfirmation({
				title: 'Delete Canvas',
				content: errorMessage?.Item2,
				onConfirm: handleOnConfirmDeleteObject,
			});
		}
	};

	const handleOnConfirmDeleteObject = async (objectSelected: ObjectLibraryTreeNode) => {
		setIsLoading(true);
		const { objectId } = objectSelected.extendedData;
		const response = await graphicApi.deleteGraphic(objectId);

		if (handleResponse(response)) {
			return;
		}

		setIsLoading(false);
	};

	const handleOnSearch = (searchName: string) => {
		searchObjectLibrary(searchName);
	};

	const searchObjectLibrary = useCallback(
		debounce(async (searchedName: string) => {
			const searchItems = findMockedNodes(objectLibrary);
			if (searchItems.length) {
				setIsSearchLoading(true);
				const response = await graphicApi.searchObjectLibrary(
					searchedName,
					searchItems.map(x => ({
						ObjectId: x.extendedData.objectId,
						ObjectType: x.extendedData.objectType,
						ParentId: x.parentNode.extendedData.objectId,
						PortStyle: x.extendedData.portStyle,
					}))
				);
				if (handleResponse(response)) {
					dispatch(setErrorMessageAction(response.ErrorMessage));
					setIsSearchLoading(false);
				} else {
					const rootNode = { ...objectLibrary };
					response.Entity.forEach(x => {
						if (x.children.length) {
							const folderNode = findNode(rootNode, {
								ObjectType: x.extendedData.objectType,
								ParentId: x.extendedData.objectId,
								ObjectId: 0,
								PortStyle: x.extendedData.portStyle,
							});
							folderNode.extendedData.hasMockedData = false;
							folderNode.children = [...x.children];
							reassignParentNode(folderNode, folderNode.parentNode, folderNode.isChecked);
						}
					});

					const searchedNodes: React.Key[] = keywordFilter(rootNode, searchValue);
					batch(() => {
						setObjectLibrary(rootNode);
						setAutoExpandParent(true);
						setExpandedKeys(searchedNodes);
						setIsSearchLoading(false);
					});
				}
			}
		}, 200),
		[searchValue]
	);

	const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchValue(value);
	};

	const handleOnDragStart = (dragEvent: NodeDragEvent) => {
		const { event } = dragEvent;
		const dataNode = dragEvent.node as ObjectLibraryTreeNode;

		if (dataNode.folderType == ObjectLibraryFolder.FOLDER) {
			return;
		}

		const json = getDropDataNode(dataNode);
		const jsonString = JSON.stringify(json);

		const dragImage: HTMLImageElement = dragImages.find(x => x.Key === dataNode.extendedData.objectType)?.Value;
		if (dragImage) {
			event.dataTransfer.setDragImage(dragImage, 0, 0);
		}

		event.dataTransfer.setData('custom-type', jsonString);
	};

	const getDropDataNode = (dataNode: ObjectLibraryTreeNode): Partial<ObjectLibraryTreeNode> => {
		const dropNodeObj: Partial<ObjectLibraryTreeNode> = {
			displayName: dataNode.displayName,
			children: [],
			parentNode: null,
			extendedData: {
				...dataNode.extendedData,
			},
		};

		return dropNodeObj;
	};

	// TODO: Disable the folder that doesn't support drag and drop actions
	// TODO | implement a way to identify how many and which nodes have been added to the canvas
	const handleOnClickAddToMap = () => {
		dispatch(setDropDataNodesAction(checkedDropDataNodes.filter(x => x.folderType !== ObjectLibraryFolder.FOLDER)));
		batch(() => {
			setCheckedDropDataNodes([]);
			resetCheckedObjectLibraryTree(objectLibrary);
			setObjectLibrary(objectLibrary);
		});
	};

	const handleOnCloseButton = () => {
		const setting: Partial<FloatingWindowsVisibility> = isObjectDirectory
			? {
					devices: false,
			  }
			: {
					plottedDevices: false,
			  };

		dispatch(switchFloatingWindowVisibilityAction(setting));
	};

	const handleOnChangeDeviceCheckbox = (event: CheckboxChangeEvent, node: ObjectLibraryTreeNode) => {
		const { checked } = event.target;

		const rootNode = { ...objectLibrary };
		const newNode = findNode(rootNode, {
			ObjectType: node.extendedData.objectType,
			ParentId: node.parentNode.extendedData.objectId,
			ObjectId: node.extendedData.objectId,
			PortStyle: node.extendedData.portStyle,
		});
		newNode.isChecked = checked;
		if (newNode.folderType === ObjectLibraryFolder.FOLDER) {
			let searchItems: ObjectLibraryTreeNode[];
			searchItems = findMockedNodes(node, []);
			if (newNode.extendedData.hasMockedData) {
				graphicApi
					.getObjectsLibraryExpanded(newNode.parentNode.extendedData.objectId, newNode.extendedData.objectType, newNode.extendedData.portStyle)
					.then(response => {
						if (handleResponse(response)) {
							dispatch(setErrorMessageAction(response.ErrorMessage));
							return;
						}
						const expandedNode = findNode(rootNode, {
							ObjectType: newNode.extendedData.objectType,
							ParentId: newNode.parentNode.extendedData.objectId,
							ObjectId: newNode.extendedData.objectId,
							PortStyle: newNode.extendedData.portStyle,
						});
						expandedNode.extendedData.hasMockedData = false;
						expandedNode.children = [...response.Entity];
						reassignParentNode(expandedNode, expandedNode.parentNode, expandedNode.isChecked, false);
						updateAllChildren(expandedNode, checked, []);
						isAllChildrenSelected(expandedNode);
						updateParentIsAllChildrenSelected(expandedNode?.parentNode);
						const newDropDataNodes: ObjectLibraryTreeNode[] = checked
							? [
									...checkedDropDataNodes,
									{
										...expandedNode,
										children: [],
										parentNode: null,
									},
									...response.Entity.map(x => ({ ...x, children: [], parentNode: null })),
							  ]
							: checkedDropDataNodes.filter(x => x.key !== expandedNode.key.toString());
						setCheckedDropDataNodes(newDropDataNodes);
						setObjectLibrary(rootNode);
					});
			} else {
				updateAllChildren(node, checked, []);
				isAllChildrenSelected(node);
				updateParentIsAllChildrenSelected(node?.parentNode);
				const keysToExclude: string[] = [node.key.toString(), ...node.children.map(x => x.key.toString())];
				const newDropDataNodes: ObjectLibraryTreeNode[] = checked
					? [
							...checkedDropDataNodes,
							{
								...node,
								children: [],
								parentNode: null,
							},
							...node.children.map(x => ({ ...x, children: [], parentNode: null })),
					  ]
					: checkedDropDataNodes.filter(x => !keysToExclude.some(y => y === x.key));
				setCheckedDropDataNodes(newDropDataNodes);
				setObjectLibrary(rootNode);
			}
		} else {
			updateParentIsAllChildrenSelected(newNode?.parentNode);
			const newDropDataNodes: ObjectLibraryTreeNode[] = checked
				? [
						...checkedDropDataNodes,
						{
							...node,
							children: [],
							parentNode: null,
						},
				  ]
				: checkedDropDataNodes.filter(x => x.key !== node.key.toString());
			setCheckedDropDataNodes(newDropDataNodes);
			setObjectLibrary(rootNode);
		}
	};

	const hasCheckedKeys = !!checkedDropDataNodes.length;
	let isIndeterminateTopCheckbox = false;
	if (objectLibrary) {
		isIndeterminateTopCheckbox = hasCheckedKeys && !objectLibrary.allChildrenSelected;
	}

	return (
		<>
			<div id={`graphic${controllerControlName}`} className={styles.objectDirectory}>
				<div id={`graphic${controllerControlName}Container`} className={styles.objectDirectoryContainer}>
					<div id={`graphic${controllerControlName}Header`} className={styles.objectDirectoryHeader}>
						{isObjectDirectory && (
							<div id={`graphic${controllerControlName}DevicesTreeBannerCheckbox`} className={styles.devicesBannerCheckbox}>
								<Checkbox disabled={true} indeterminate={isIndeterminateTopCheckbox} checked={hasCheckedKeys} />
							</div>
						)}
						<div className={styles.devicesTitle}>
							<label className={styles.bannerLabel}>{isObjectDirectory ? _('Devices') : _('PlottedDevices')}</label>
						</div>
						<div>
							<Button
								onClick={handleOnCloseButton}
								className={styles.bannerButton}
								type='text'
								size='small'
								icon={<CloseOutlined style={{ fontSize: '12px' }} />}
							/>
						</div>
					</div>
					<div id={`graphic${controllerControlName}Content`} className={styles.objectDirectoryContent}>
						<div id={`graphic${controllerControlName}SearchBar`} className={styles.searchBarContainer}>
							<Search
								id={`graphic${controllerControlName}SearchBarInput`}
								className={styles.searchBar}
								placeholder={_('SearchComponentsByName')}
								onSearch={handleOnSearch}
								enterButton={true}
								loading={isSearchLoading}
								onChange={handleOnChangeSearch}
								value={searchValue}
								size='small'
							/>
							{isObjectDirectory && (
								<Button
									type='primary'
									className={styles.addToMapButton}
									onClick={handleOnClickAddToMap}
									disabled={!hasCheckedKeys}
									size='small'
									title={_('AddToMap')}>
									<PlusOutlined style={{ fontSize: '12px' }} />
								</Button>
							)}
						</div>
						<div id={`graphic${controllerControlName}DevicesTree`} className={styles.devicesTreeContainer}>
							<div
								id={`graphic${controllerControlName}TreeContainer`}
								className={cx(styles.devicesTree, {
									[styles.disabledTree]: isLoading,
								})}
								onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
								<Spin wrapperClassName={styles.spin} tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
									<DirectoryTree
										allowDrop={() => false}
										draggable={isObjectDirectory}
										autoExpandParent={autoExpandParent}
										height={500}
										treeData={mapDeviceRenderNode({
											isObjectDirectory,
											treeData: objectLibrary?.children,
											searchValue,
											expandedKeys,
											onClickContextMenu: handleOnClickContextMenu,
											onChangeDeviceCheckbox: handleOnChangeDeviceCheckbox,
										})}
										expandAction={false}
										expandedKeys={expandedKeys}
										disabled={false}
										motion={false}
										selectedKeys={[]}
										onExpand={handleOnExpand}
										onDragStart={handleOnDragStart}
									/>
								</Spin>
							</div>
						</div>
					</div>
				</div>
			</div>
			{currentDeviceForConfiguration}
		</>
	);
};

export { ObjectLibrary };
